import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import { startAuthFlow, logout } from './auth';

// ── SEO 설정 기본값 ──────────────────────────────────────────────────
const SETTINGS_DEFAULTS = {
  title: 'SmartFinance — ISA · 연금저축 · IRP 절세 전략',
  description: 'ISA · 연금저축 · IRP 절세 전략을 데이터 기반으로 분석하는 스마트 금융 플래너',
  favicon: '',
  ogTitle: '', ogDescription: '', ogImage: '', ogUrl: '',
  twitterCard: 'summary_large_image',
  twitterTitle: '', twitterDescription: '', twitterImage: '', twitterSite: '',
  googleVerify: '', naverVerify: '', daumVerify: '',
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function applyMetaSettings(s) {
  if (s.title) document.title = s.title;

  const upsert = (keyAttr, key, val) => {
    if (!val) return;
    let el = document.querySelector(`meta[${keyAttr}="${key}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(keyAttr, key);
      document.head.appendChild(el);
    }
    el.setAttribute('content', val);
  };

  if (s.favicon) {
    let link = document.querySelector("link[rel='icon'], link[rel='shortcut icon']");
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
    link.href = s.favicon;
  }

  upsert('name', 'description', s.description);
  upsert('property', 'og:title', s.ogTitle || s.title);
  upsert('property', 'og:description', s.ogDescription || s.description);
  upsert('property', 'og:image', s.ogImage);
  upsert('property', 'og:url', s.ogUrl);
  upsert('property', 'og:type', 'website');
  upsert('name', 'twitter:card', s.twitterCard || 'summary_large_image');
  upsert('name', 'twitter:title', s.twitterTitle || s.ogTitle || s.title);
  upsert('name', 'twitter:description', s.twitterDescription || s.ogDescription || s.description);
  upsert('name', 'twitter:image', s.twitterImage || s.ogImage);
  if (s.twitterSite) upsert('name', 'twitter:site', s.twitterSite);
  if (s.googleVerify) upsert('name', 'google-site-verification', s.googleVerify);
  if (s.naverVerify) upsert('name', 'naver-site-verification', s.naverVerify);
  if (s.daumVerify) upsert('name', 'daum-site-verification', s.daumVerify);
}

// ── SiteHeader ───────────────────────────────────────────────────────
export function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const isSimulator = location.pathname === '/simulator';
  const { user, setUser, isSuwannews, loading } = useContext(AuthContext);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => {
    logout();
    setUser(null);
    setDropdownOpen(false);
  };

  const username = user?.login || user?.user_login || user?.preferred_username || user?.nickname || '';
  const avatarLetter = username.charAt(0).toUpperCase();
  const alwaysOpaque = isSimulator;

  return (
    <header
      className="fixed top-0 left-0 right-0 transition-all duration-300"
      style={{
        zIndex: 50,
        ...(scrolled || alwaysOpaque
          ? { background: 'rgba(6,12,26,0.85)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', borderBottom: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }
          : { background: 'transparent' }),
      }}
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6 flex items-center justify-between" style={{ height: 64 }}>
        <Link to="/" className="flex items-center gap-2 flex-shrink-0" style={{ textDecoration: 'none' }}>
          <span className="text-white font-black text-base px-2 py-0.5 rounded-lg"
            style={{ background: 'linear-gradient(135deg,#3b82f6,#6366f1)' }}>FP</span>
          <span className="text-white font-black text-lg hidden sm:inline">SmartFinance</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {[['/#products','제품소개'],['/#synergy','시너지전략'],['/#articles','기사']].map(([href, label]) => (
            <a key={href} href={href} className="text-sm font-medium transition-colors"
              style={{ color: 'rgba(255,255,255,0.65)', textDecoration: 'none' }}
              onMouseEnter={e => (e.target.style.color = '#fff')}
              onMouseLeave={e => (e.target.style.color = 'rgba(255,255,255,0.65)')}>
              {label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {!loading && (
            <div className="relative" ref={dropdownRef}>
              {/* 아바타 버튼 */}
              <button
                onClick={() => setDropdownOpen(v => !v)}
                className="flex items-center justify-center w-9 h-9 rounded-full font-black text-sm select-none transition-all"
                style={user
                  ? { background: 'linear-gradient(135deg,#3b82f6,#6366f1)', color: '#fff', boxShadow: dropdownOpen ? '0 0 0 2px rgba(99,102,241,0.7),0 0 16px rgba(99,102,241,0.4)' : '0 0 14px rgba(99,102,241,0.4)' }
                  : { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.14)' }
                }
              >
                {user ? avatarLetter : (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                )}
              </button>

              {/* 드롭다운 */}
              {dropdownOpen && (
                <div
                  className="absolute right-0 rounded-2xl overflow-hidden"
                  style={{
                    top: 'calc(100% + 10px)', minWidth: 176,
                    background: 'rgba(6,12,26,0.97)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
                    border: '1px solid rgba(255,255,255,0.13)', boxShadow: '0 16px 48px rgba(0,0,0,0.55)', zIndex: 200,
                  }}
                >
                  {user ? (
                    <>
                      <div className="px-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        {isSuwannews && (
                          <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-lg mb-1.5"
                            style={{ background: 'rgba(16,185,129,0.15)', color: '#34d399', border: '1px solid rgba(16,185,129,0.3)' }}>
                            ✏️ 편집자
                          </span>
                        )}
                        <p className="text-sm font-bold text-white truncate">{username}</p>
                      </div>
                      <div className="p-1.5">
                        <button onClick={handleLogout}
                          className="w-full text-left text-sm px-3 py-2 rounded-xl transition-colors"
                          style={{ color: 'rgba(255,255,255,0.6)' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = '#fff'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = ''; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}>
                          로그아웃
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="p-1.5">
                      <button
                        onClick={() => { startAuthFlow(); setDropdownOpen(false); }}
                        className="w-full text-left text-sm font-bold px-3 py-2 rounded-xl transition-colors"
                        style={{ color: 'rgba(255,255,255,0.8)' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = '#fff'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = ''; e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; }}>
                        로그인
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <Link to="/simulator" className="font-bold text-sm px-4 py-2 rounded-xl transition-all flex-shrink-0"
            style={{ background: 'linear-gradient(135deg,#3b82f6,#6366f1)', color: '#fff', textDecoration: 'none', boxShadow: '0 0 20px rgba(99,102,241,0.35)' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 28px rgba(99,102,241,0.55)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 0 20px rgba(99,102,241,0.35)'; }}>
            분석기 →
          </Link>
        </div>
      </div>
    </header>
  );
}

// ── SiteFooter ───────────────────────────────────────────────────────
export function SiteFooter() {
  return (
    <footer className="relative mt-10"
      style={{ zIndex: 10, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <span className="font-black text-base px-2 py-0.5 rounded-lg"
                style={{ background: 'linear-gradient(135deg,#3b82f6,#6366f1)', color: '#fff' }}>FP</span>
              <span className="text-white font-black text-lg">SmartFinance</span>
            </div>
            <p className="text-sm leading-relaxed max-w-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
              ISA · 연금저축 · IRP 절세 전략을 데이터 기반으로 분석하는 스마트 금융 플래너입니다.
            </p>
          </div>
          <div>
            <p className="font-bold text-sm mb-4 text-white" style={{ opacity: 0.8 }}>서비스</p>
            <ul className="space-y-2 text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
              {[['/#products','제품소개'],['/#synergy','시너지전략']].map(([h,l]) => (
                <li key={h}><a href={h} style={{ color:'inherit', textDecoration:'none' }}
                  onMouseEnter={e=>(e.target.style.color='#fff')}
                  onMouseLeave={e=>(e.target.style.color='rgba(255,255,255,0.45)')}>{l}</a></li>
              ))}
              <li><Link to="/simulator" style={{ color:'inherit', textDecoration:'none' }}
                onMouseEnter={e=>(e.currentTarget.style.color='#fff')}
                onMouseLeave={e=>(e.currentTarget.style.color='rgba(255,255,255,0.45)')}>분석기</Link></li>
            </ul>
          </div>
          <div>
            <p className="font-bold text-sm mb-4 text-white" style={{ opacity: 0.8 }}>정보</p>
            <ul className="space-y-2 text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
              <li><a href="/#articles" style={{ color:'inherit', textDecoration:'none' }}
                onMouseEnter={e=>(e.target.style.color='#fff')}
                onMouseLeave={e=>(e.target.style.color='rgba(255,255,255,0.45)')}>기사 &amp; 인사이트</a></li>
              <li><span style={{ color:'rgba(255,255,255,0.25)' }}>개인정보처리방침</span></li>
              <li><span style={{ color:'rgba(255,255,255,0.25)' }}>이용약관</span></li>
            </ul>
          </div>
        </div>
        <div className="flex flex-col md:flex-row justify-between items-center gap-3 pt-6"
          style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>
            © 2025 SmartFinance. Data source: Local SQLite DB (Simulated).
          </p>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>
            본 서비스는 투자 권유가 아닌 정보 제공 목적입니다.
          </p>
        </div>
      </div>
    </footer>
  );
}

// ── 입력 필드 컴포넌트 ───────────────────────────────────────────────
function Field({ label, value, onChange, placeholder, hint }) {
  return (
    <div className="mb-4">
      <label className="block text-xs font-bold mb-1.5" style={{ color: 'rgba(255,255,255,0.55)' }}>{label}</label>
      <input type="text" value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder={placeholder || ''}
        className="w-full px-3 py-2.5 rounded-xl text-sm outline-none"
        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', transition: 'border-color 0.15s' }}
        onFocus={e => (e.currentTarget.style.borderColor = 'rgba(99,102,241,0.6)')}
        onBlur={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)')}
      />
      {hint && <p className="text-xs mt-1.5" style={{ color: 'rgba(255,255,255,0.3)' }}>{hint}</p>}
    </div>
  );
}

function TextareaField({ label, value, onChange, placeholder, hint, rows = 3 }) {
  return (
    <div className="mb-4">
      <label className="block text-xs font-bold mb-1.5" style={{ color: 'rgba(255,255,255,0.55)' }}>{label}</label>
      <textarea value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder={placeholder || ''} rows={rows}
        className="w-full px-3 py-2.5 rounded-xl text-sm outline-none resize-none"
        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', transition: 'border-color 0.15s' }}
        onFocus={e => (e.currentTarget.style.borderColor = 'rgba(99,102,241,0.6)')}
        onBlur={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)')}
      />
      {hint && <p className="text-xs mt-1.5" style={{ color: 'rgba(255,255,255,0.3)' }}>{hint}</p>}
    </div>
  );
}

// ── SiteSettings (플로팅 SEO 설정 버튼 + 모달) ───────────────────────
export function SiteSettings() {
  const { isSuwannews } = useContext(AuthContext);

  const [settings, setSettings] = useState(SETTINGS_DEFAULTS);
  const [draft, setDraft]       = useState(SETTINGS_DEFAULTS);
  const [open, setOpen]         = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);

  // 모든 사용자에 대해 저장된 설정을 로드하고 적용
  useEffect(() => {
    if (!BACKEND) { applyMetaSettings(SETTINGS_DEFAULTS); return; }
    fetch(`${BACKEND}/api/site-settings`)
      .then(r => r.ok ? r.json() : {})
      .then(data => {
        const merged = { ...SETTINGS_DEFAULTS, ...(data || {}) };
        setSettings(merged);
        setDraft(merged);
        applyMetaSettings(merged);
      })
      .catch(() => applyMetaSettings(SETTINGS_DEFAULTS));
  }, []); // eslint-disable-line

  const openModal = () => {
    setDraft({ ...settings });
    setActiveTab('basic');
    setSaved(false);
    setOpen(true);
  };

  const u = (key, val) => setDraft(d => ({ ...d, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('sf_access_token');
      const res = await fetch(`${BACKEND}/api/site-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(draft),
      });
      if (res.ok) {
        setSettings(draft);
        applyMetaSettings(draft);
        setSaved(true);
        setTimeout(() => { setSaved(false); setOpen(false); }, 1400);
      }
    } catch (e) { /* silent */ }
    finally { setSaving(false); }
  };

  if (!isSuwannews) return null;

  const TABS = [
    { id: 'basic',     label: '기본' },
    { id: 'favicon',   label: '파비콘' },
    { id: 'og',        label: '오픈그라프' },
    { id: 'twitter',   label: 'X (Twitter)' },
    { id: 'webmaster', label: '웹마스터' },
  ];

  return (
    <>
      {/* 플로팅 버튼 */}
      <button
        onClick={openModal}
        className="fixed flex items-center justify-center w-12 h-12 rounded-2xl no-print"
        title="사이트 SEO 설정"
        style={{ bottom: 28, right: 28, zIndex: 900, background: 'linear-gradient(135deg,#3b82f6,#6366f1)', boxShadow: '0 4px 20px rgba(99,102,241,0.5)', transition: 'transform 0.18s, box-shadow 0.18s' }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.12)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(99,102,241,0.7)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 4px 20px rgba(99,102,241,0.5)'; }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3"/>
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
        </svg>
      </button>

      {/* 모달 오버레이 */}
      {open && (
        <div
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{ zIndex: 1000, background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(6px)' }}
          onClick={e => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="w-full flex flex-col" style={{ maxWidth: 520, maxHeight: '88vh', background: 'rgba(6,12,26,0.98)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 28, boxShadow: '0 32px 80px rgba(0,0,0,0.7)' }}>

            {/* 헤더 */}
            <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <div>
                <h2 className="text-lg font-black text-white">사이트 SEO 설정</h2>
                <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>메타 태그 · 파비콘 · 웹마스터 도구</p>
              </div>
              <button onClick={() => setOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full text-sm"
                style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.5)' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.14)'; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}>✕</button>
            </div>

            {/* 탭 */}
            <div className="flex gap-1.5 px-6 pt-4 pb-1 flex-shrink-0 overflow-x-auto">
              {TABS.map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className="flex-shrink-0 text-xs font-bold px-3 py-1.5 rounded-xl transition-all"
                  style={activeTab === tab.id
                    ? { background: 'linear-gradient(135deg,#3b82f6,#6366f1)', color: '#fff' }
                    : { background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.45)', border: '1px solid rgba(255,255,255,0.08)' }
                  }>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* 탭 콘텐츠 */}
            <div className="flex-1 overflow-y-auto px-6 py-5">

              {activeTab === 'basic' && <>
                <Field label="사이트 제목 (title)" value={draft.title} onChange={v => u('title', v)}
                  placeholder="SmartFinance — ISA · 연금저축 · IRP 절세 전략"
                  hint="브라우저 탭과 검색 결과에 표시됩니다." />
                <TextareaField label="사이트 설명 (description)" value={draft.description} onChange={v => u('description', v)}
                  placeholder="ISA · 연금저축 · IRP 절세 전략을 데이터 기반으로..."
                  hint="검색 결과 스니펫에 표시됩니다. 120–160자 권장." rows={3} />
              </>}

              {activeTab === 'favicon' && <>
                <Field label="파비콘 URL" value={draft.favicon} onChange={v => u('favicon', v)}
                  placeholder="https://moneyroute.swn.kr/favicon.ico"
                  hint="PNG, ICO, SVG 형식 권장 — 32×32 또는 64×64px" />
                {draft.favicon && (
                  <div className="flex items-center gap-3 mt-1 p-3 rounded-xl"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <img src={draft.favicon} alt="favicon 미리보기"
                      style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'contain', background: '#fff' }}
                      onError={e => { e.target.style.display = 'none'; }} />
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>파비콘 미리보기</span>
                  </div>
                )}
              </>}

              {activeTab === 'og' && <>
                <p className="text-xs rounded-xl px-3 py-2.5 mb-4" style={{ background: 'rgba(59,130,246,0.08)', color: 'rgba(255,255,255,0.45)', border: '1px solid rgba(59,130,246,0.15)' }}>
                  Facebook · KakaoTalk 등 SNS 공유 시 표시되는 정보입니다.
                </p>
                <Field label="OG 제목" value={draft.ogTitle} onChange={v => u('ogTitle', v)} placeholder="비워두면 기본 제목 사용" />
                <TextareaField label="OG 설명" value={draft.ogDescription} onChange={v => u('ogDescription', v)} placeholder="비워두면 기본 설명 사용" rows={2} />
                <Field label="OG 이미지 URL" value={draft.ogImage} onChange={v => u('ogImage', v)}
                  placeholder="https://moneyroute.swn.kr/og-image.jpg" hint="권장: 1200×630px" />
                <Field label="OG URL (정규 주소)" value={draft.ogUrl} onChange={v => u('ogUrl', v)} placeholder="https://moneyroute.swn.kr" />
              </>}

              {activeTab === 'twitter' && <>
                <p className="text-xs rounded-xl px-3 py-2.5 mb-4" style={{ background: 'rgba(99,102,241,0.08)', color: 'rgba(255,255,255,0.45)', border: '1px solid rgba(99,102,241,0.15)' }}>
                  X (구 Twitter) 공유 카드 설정입니다.
                </p>
                <div className="mb-4">
                  <label className="block text-xs font-bold mb-1.5" style={{ color: 'rgba(255,255,255,0.55)' }}>카드 유형</label>
                  <div className="flex gap-2">
                    {[['summary','Summary (작은 이미지)'],['summary_large_image','Large Image (큰 이미지)']].map(([val, label]) => (
                      <button key={val} onClick={() => u('twitterCard', val)}
                        className="flex-1 py-2.5 px-2 rounded-xl text-xs font-bold transition-all"
                        style={draft.twitterCard === val
                          ? { background: 'linear-gradient(135deg,#3b82f6,#6366f1)', color: '#fff' }
                          : { background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.45)', border: '1px solid rgba(255,255,255,0.08)' }}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <Field label="Twitter 제목" value={draft.twitterTitle} onChange={v => u('twitterTitle', v)} placeholder="비워두면 OG 제목 사용" />
                <TextareaField label="Twitter 설명" value={draft.twitterDescription} onChange={v => u('twitterDescription', v)} placeholder="비워두면 OG 설명 사용" rows={2} />
                <Field label="Twitter 이미지 URL" value={draft.twitterImage} onChange={v => u('twitterImage', v)} placeholder="비워두면 OG 이미지 사용" />
                <Field label="Twitter 계정" value={draft.twitterSite} onChange={v => u('twitterSite', v)} placeholder="@suwannews" />
              </>}

              {activeTab === 'webmaster' && <>
                <p className="text-xs rounded-xl px-3 py-2.5 mb-4" style={{ background: 'rgba(16,185,129,0.08)', color: 'rgba(255,255,255,0.45)', border: '1px solid rgba(16,185,129,0.15)' }}>
                  각 검색엔진 웹마스터 도구의 소유권 확인 코드를 입력하세요.
                </p>
                <Field label="Google Search Console" value={draft.googleVerify} onChange={v => u('googleVerify', v)}
                  placeholder="ABC123xyz..."
                  hint="google-site-verification — content 값만 입력" />
                <Field label="네이버 서치어드바이저" value={draft.naverVerify} onChange={v => u('naverVerify', v)}
                  placeholder="abc123..."
                  hint="naver-site-verification — content 값만 입력" />
                <Field label="Daum 웹마스터" value={draft.daumVerify} onChange={v => u('daumVerify', v)}
                  placeholder="verification 코드"
                  hint="daum-site-verification — content 값만 입력" />
              </>}
            </div>

            {/* 푸터 */}
            <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.22)' }}>저장 시 즉시 적용됩니다</span>
              <div className="flex gap-2">
                <button onClick={() => setOpen(false)}
                  className="px-4 py-2 rounded-xl text-sm font-bold"
                  style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.1)' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = 'rgba(255,255,255,0.55)'; }}>
                  취소
                </button>
                <button onClick={handleSave} disabled={saving || saved}
                  className="px-5 py-2 rounded-xl text-sm font-bold transition-all"
                  style={{
                    background: saved ? 'linear-gradient(135deg,#10b981,#059669)' : 'linear-gradient(135deg,#3b82f6,#6366f1)',
                    color: '#fff', opacity: saving ? 0.7 : 1,
                    boxShadow: saved ? '0 4px 16px rgba(16,185,129,0.4)' : '0 4px 16px rgba(99,102,241,0.3)',
                  }}>
                  {saved ? '✓ 저장됨' : saving ? '저장 중...' : '저장'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
